/**
 * Shared react-grid-layout sizing constants — used by WidgetHost (T5, for
 * WidgetBoundary's error-fallback minHeight), DashboardGrid/edit-mode
 * (T6/T8, for the actual grid configuration), and MoveResizeModal (the
 * keyboard resize path, which must enforce the SAME floors as RGL's mouse
 * path). rowHeight 72 / margin [16,16] per the design spec's "rowHeight ~72"
 * line.
 */
import type { BuiltinWidget } from '@/types/domain';

export const ROW_HEIGHT = 72;
export const GRID_MARGIN: [number, number] = [16, 16];

/** Base w/h floors every widget gets (RGL minW/minH + the keyboard path). */
export const WIDGET_MIN_W = 2;
export const WIDGET_MIN_H = 2;

/**
 * Per-kind minH overrides for builtins whose content has a fixed vertical
 * footprint the base floor can't hold — the donut widgets render a hardcoded
 * 180x180 ring plus header, which bleeds past a 2-row (~160px) card
 * (widget overflow/clip floors audit finding 7).
 */
export const WIDGET_MIN_H_BY_KIND: Partial<Record<BuiltinWidget, number>> = {
  STATUS_DONUT: 3,
  HEALTH_DONUT: 3,
  // Top-5 member list + fully-booked strip + footer need ~400px; h:5 = 424px
  // is the smallest size that honours the panel's content contract (h:4's
  // 336px silently clips rows behind the overflow guard).
  CAPACITY_PANEL: 5,
};

/** Resolved minH for a widget: the per-kind override when one exists, else the base floor. */
export function widgetMinH(builtinRef: BuiltinWidget | null): number {
  return (builtinRef && WIDGET_MIN_H_BY_KIND[builtinRef]) || WIDGET_MIN_H;
}

/**
 * Builtins whose tile height is CONTENT-driven (their resolved data-row
 * count) rather than the widget's stored grid_h — DashboardGrid supersedes
 * gridH for these two kinds at render time (see gridRowsForListRows below).
 * Purely presentational: nothing is written back to the stored grid_h for
 * these widgets, and there is no migration — a widget stored at the old
 * fixed h:3 simply renders at whatever gridRowsForListRows computes once its
 * Host reports a row count.
 */
export const AUTO_HEIGHT_BUILTINS = new Set<BuiltinWidget>(['ACCOUNTS_TABLE', 'PROJECT_RAG_TABLE']);

/** Clamp bounds for gridRowsForListRows — see its own doc comment. */
export const MIN_LIST_GRID_ROWS = 2;
export const MAX_LIST_GRID_ROWS = 8;

/**
 * Fixed per-card chrome the two ledger-table builtins pay regardless of row
 * count — CardHeader's own box plus the 12px marginBottom the Hosts set
 * around it, the ledger's sticky <thead> row, and a breathing-room padding
 * budget — plus one LIST_TABLE_ROW_PX per data row. Named so the arithmetic
 * in gridRowsForListRows reads as content math, not a bag of magic numbers.
 */
export const LIST_CARD_HEADER_PX = 52;
export const LIST_TABLE_HEADER_PX = 40;
export const LIST_TABLE_ROW_PX = 40;
export const LIST_CARD_PADDING_PX = 20;

/**
 * Pixels RGL actually allocates to an item spanning `h` grid rows — mirrors
 * react-grid-layout's own calcGridItemWHPx(h, ROW_HEIGHT, marginY): h row-
 * heights plus (h-1) margins between them (never a margin past the last
 * row). h:3 → 72*3 + 16*2 = 248px, matching the ~248px the old fixed-h:3
 * ledger tiles rendered at today.
 */
function gridRowsToPx(h: number): number {
  return ROW_HEIGHT * h + GRID_MARGIN[1] * Math.max(0, h - 1);
}

/**
 * Convert a ledger widget's resolved data-row count into the smallest grid-
 * row span in [MIN_LIST_GRID_ROWS..MAX_LIST_GRID_ROWS] whose real pixel
 * height (gridRowsToPx) can hold the content — CardHeader + table header +
 * one row per record + padding — without clipping it. A single-record table
 * is no longer stretched to the old fixed h:3 ("why so big for one
 * record?"), a mid-size list grows a row or two, and a long list caps out at
 * MAX_LIST_GRID_ROWS and scrolls internally (AccountsTable/ProjectRagTable
 * both already set overflowY:auto) instead of growing the tile without
 * bound. Pure and deterministic — the same row count always yields the same
 * grid height — so the "settle" DashboardGrid drives off it is a single
 * computed value applied once data arrives, never a running per-row
 * animation.
 */
export function gridRowsForListRows(dataRows: number): number {
  const rows = Math.max(0, dataRows);
  const contentPx = LIST_CARD_HEADER_PX + LIST_TABLE_HEADER_PX + LIST_CARD_PADDING_PX + rows * LIST_TABLE_ROW_PX;
  for (let h = MIN_LIST_GRID_ROWS; h < MAX_LIST_GRID_ROWS; h++) {
    if (gridRowsToPx(h) >= contentPx) return h;
  }
  return MAX_LIST_GRID_ROWS;
}
